import { useState, useEffect, useRef } from 'react';

// Debounce hook
const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => clearTimeout(handler);
    }, [value, delay]);

    return debouncedValue;
};

/**
 * Hook to safely observe and track a DOM element by selector
 * @param {string} selector - CSS selector to find the element
 * @param {number} [debounceDelay=200] - Debounce delay in milliseconds
 * @returns {HTMLElement|null} The found element or null
 */
export const useContainerElement = (selector, debounceDelay = 200) => {
    const [element, setElement] = useState(null);
    const observerRef = useRef(null);
    const lastSelector = useRef(selector);
    const debouncedSelector = useDebounce(selector, debounceDelay);

    // Element finding logic
    useEffect(() => {
        if (debouncedSelector !== lastSelector.current) {
            lastSelector.current = debouncedSelector;
            setElement(null);
        }

        const findElement = () => {
            const domElement = document.querySelector(debouncedSelector);
            if (domElement && domElement !== element) {
                // Ensure we only set this once to avoid re-renders
                setElement(domElement);
                return true;
            }
            return false;
        };

        // Try to find element immediately
        if (!findElement()) {
            // If not found, set up observer without duplicating
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
            
            observerRef.current = new MutationObserver((mutations) => {
                for (const mutation of mutations) {
                    if (mutation.addedNodes.length) {
                        if (findElement()) {
                            observerRef.current?.disconnect();
                            break;
                        }
                    }
                }
            });

            // Observe only after ensuring previous is disconnected
            observerRef.current.observe(document.body, {
                childList: true,
                subtree: true
            });
        }

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [debouncedSelector, element]);

    return element;
};

export default useContainerElement;